import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Search } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAdminSession } from '@/lib/adminAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminAuditPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = location.state?.currentUser || getAdminSession();
  
  const [auditData, setAuditData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterTarget, setFilterTarget] = useState('all');
  
  useEffect(() => {
    if (!currentUser || currentUser.role !== 'superadmin') {
      toast.error('Access denied. Super admin only.');
      navigate('/admin/waitlist');
      return;
    }
    fetchAuditLog();
  }, [currentUser, navigate]);
  
  const fetchAuditLog = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/audit`);
      setAuditData(response.data);
    } catch (error) {
      toast.error('Failed to fetch audit log');
      console.error('Fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const filteredData = auditData.filter(item => {
    const matchesSearch = item.admin_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.admin_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesAction = filterAction === 'all' || item.action === filterAction;
    const matchesTarget = filterTarget === 'all' || item.target === filterTarget;
    return matchesSearch && matchesAction && matchesTarget;
  });
  
  if (!currentUser) return null;
  
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground font-['Space_Grotesk'] mb-2">Audit Log</h1>
            <p className="text-muted-foreground">Track all admin actions and changes</p>
          </div>
          <Button 
            variant="outline" 
            onClick={() => navigate('/admin/waitlist', { state: { currentUser } })}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
        
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by admin email or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterAction} onValueChange={setFilterAction}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              <SelectItem value="edit">Edit</SelectItem>
              <SelectItem value="delete">Delete</SelectItem>
              <SelectItem value="password_change">Password Change</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterTarget} onValueChange={setFilterTarget}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Targets</SelectItem>
              <SelectItem value="waitlist">Waitlist</SelectItem>
              <SelectItem value="pilot">Pilot</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Audit Table */}
        <Card className="bg-card border-border/50 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border/50">
                  <TableHead className="text-foreground">Admin</TableHead>
                  <TableHead className="text-foreground">Action</TableHead>
                  <TableHead className="text-foreground">Target</TableHead>
                  <TableHead className="text-foreground">Entry ID</TableHead>
                  <TableHead className="text-foreground">Changes</TableHead>
                  <TableHead className="text-foreground">Timestamp</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No audit logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.map((item, index) => (
                    <TableRow key={index} className="border-border/50">
                      <TableCell className="font-medium text-foreground">
                        {item.admin_name}
                        <div className="text-xs text-muted-foreground">{item.admin_email}</div>
                      </TableCell>
                      <TableCell>
                        <Badge className="capitalize">{item.action.replace('_', ' ')}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">{item.target}</Badge>
                      </TableCell>
                      <TableCell className="text-foreground text-xs">{item.entry_id || '-'}</TableCell>
                      <TableCell className="text-foreground text-xs max-w-xs truncate" title={item.changes}>
                        {item.changes || '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {new Date(item.timestamp).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
        
        <div className="mt-4 text-sm text-muted-foreground">
          Total actions: {filteredData.length}
        </div>
      </div>
    </div>
  );
}